import { EVENT_DELAY } from '@src/config/constants'
import { CONFIG_KEY_FB_PIXEL_ID } from '@src/config/env'
import { getFbPixelAutoConfigExperimentValue } from '@src/experiments/statsig-integration/get-fb-pixel-autoconfig-experiment-value'
import { getLocaleConfigFromMemory } from '@src/modules/global-state/locale-config.browser'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { initializeScript } from '@src/telemetry/integrations/provider-utils/initialize-script'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { getStorefrontIdProperty } from '@src/utils/b2b'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

// Additional Facebook Pixel IDs to fire alongside the primary CONFIG_KEY_FB_PIXEL_ID.
// Order is preserved: each pixel is `init`-ed here and then receives a `trackSingle` PageView
// (with the shared event id) from the PAGE_VIEW event handler.
const ADDITIONAL_FB_PIXEL_IDS = [
  '1900161340840474', // Blotout V2
  '458941500243684', // Forked V4
] as const

const getUserTrackingData = () => {
  return {
    em:
      getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL)?.trim() ??
      getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL)?.trim() ??
      '',
    external_id: getlbUserId()?.trim() ?? '',
  }
}

// Per-locale gate for the secondary Blotout/Forked pixels (see `additionalFbPixelsAllowed`
// in StatsigLocaleDynamicConfig). Default true for US/CA, false for UK — only the primary
// CONFIG_KEY_FB_PIXEL_ID fires for UK. `?? true` keeps legacy behaviour when the flag is
// absent (e.g. unmocked tests fall back to en-US default which is true).
const getAllFbPixelIds = (): string[] => {
  const additionalFbPixelsAllowed = getLocaleConfigFromMemory().additionalFbPixelsAllowed ?? true
  return additionalFbPixelsAllowed ? [CONFIG_KEY_FB_PIXEL_ID, ...ADDITIONAL_FB_PIXEL_IDS] : [CONFIG_KEY_FB_PIXEL_ID]
}

// Load fbevents.js on the main thread and initialise directly.
const initializeFbPixelOnMainThread = async (allPixelIds: readonly string[]) => {
  await initializeScript({
    key: 'tracking-provider-fb',
    domain: TELEMETRY_DOMAINS.FB_PIXEL,
    path: '/en_US/fbevents.js',
    injectAt: 'beforeFirstScript',
    maxDelay: 1000,
  })

  if (window.fbq) return
  window.fbq = function (...args) {
    if (window.fbq?.callMethod) {
      window.fbq.callMethod(...args)
    } else {
      window.fbq?.queue?.push(args)
    }
  }
  const fbq = window.fbq
  if (!window._fbq) window._fbq = fbq
  fbq.push = fbq
  fbq.loaded = !0
  fbq.version = '2.0'
  fbq.queue = []

  // PageView is NOT fired here — it comes from the PAGE_VIEW telemetry handler
  // (carrying the shared dedup event id). `initialize` only sets up fbq + `init`s.
  const fbPixelAutoConfig = getFbPixelAutoConfigExperimentValue()
  allPixelIds.forEach(pixelId => {
    fbq('set', 'autoConfig', fbPixelAutoConfig, pixelId)
    fbq('init', pixelId, getUserTrackingData())
  })
}

export const FbPixel: TelemetryIntegration = {
  // PageView is fired from the PAGE_VIEW event handler (not here) so it carries the shared
  // `properties.eventId` and deduplicates against the CAPI/SGTM PageView. `initialize` only
  // sets up fbq + `init`s each pixel.
  //  https://developers.facebook.com/ads/blog/post/v2/2017/05/29/tagging-a-single-page-application-facebook-pixel/
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.FB_PIXEL,
    featureGateName: 'ssr_telemetry_fb_pixel',
  },

  initialize: async () => {
    await initializeFbPixelOnMainThread(getAllFbPixelIds())
  },

  events: {
    [TELEMETRY_EVENT_NAMES.PAGE_VIEW]: properties => {
      logFbPixelPageView(properties.eventId)
    },
    [TELEMETRY_EVENT_NAMES.LEAD]: properties => {
      logFbPixelEvent({
        eventType: 'Lead',
        contentIds: [],
        contentType: 'lead',
        contentName: properties.contentName,
        contentCategory: '',
        value: 0,
      })
    },
    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: properties => {
      const value = properties?.value ?? properties?.price

      logFbPixelEvent({
        eventType: 'AddToCart',
        contentIds: [properties.variantId],
        contentType: 'product',
        contentName:
          properties.productTitle && properties.variantTitle
            ? `${String(properties.productTitle)} - ${String(properties.variantTitle)}`
            : '',
        contentCategory: properties.productType,
        value: value ? formatRevenue(Number(value)) : 0,
        numItems: properties.quantity || 1,
        eventId: (properties as { eventId?: string }).eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW](properties) {
      const isPartnerProductPDPPage = properties.isPartnerProductPDPPage
      const isSingleVariant = properties.variantIds.length === 1
      if (!isPartnerProductPDPPage && properties.productTitle) {
        if (isSingleVariant) {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: properties.variantIds.length > 0 && properties.variantIds[0] ? [properties.variantIds[0]] : [],
            contentType: 'product',
            contentName:
              properties.productTitle && properties.variantTitle
                ? properties.productTitle + ' - ' + properties.variantTitle
                : '',
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
            eventId: properties.eventId,
          })
        } else {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: [properties.productId.toString()],
            contentType: 'product_group',
            contentName: properties.productTitle,
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
            eventId: properties.eventId,
          })
        }
      }
    },

    [TELEMETRY_EVENT_NAMES.VARIANT_SELECT]: properties => {
      const isSingleVariant = properties.variantIds.length === 1
      if (!properties.is_product_card && properties.productTitle) {
        if (isSingleVariant && properties.variantIds.length > 0 && properties.variantIds[0]) {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: [properties.variantIds[0]],
            contentType: 'product',
            contentName: properties.productTitle + ' - ' + properties.variantTitle,
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
            eventId: properties.eventId,
          })
        } else {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: [properties.spid],
            contentType: 'product_group',
            contentName: properties.productTitle,
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
            eventId: properties.eventId,
          })
        }
      }
    },
  },
}

const logFbPixelEvent = ({
  eventType,
  contentIds,
  contentType,
  contentName,
  contentCategory,
  value,
  numItems,
  eventId,
}: {
  eventType: string
  contentIds: string[]
  contentType: string
  contentName: string
  contentCategory: string
  value: number
  numItems?: number
  eventId?: string
}) => {
  const localisationData = getLocalisationDataFromStorage()
  executeFireAndForgotTask(() => {
    if (window.fbq) {
      const fbEventData = {
        content_ids: contentIds,
        content_type: contentType,
        content_name: contentName,
        content_category: contentCategory,
        currency: localisationData?.currency ?? 'USD',
        country: localisationData?.country,
        value: value,
        num_items: numItems,
        event: eventType,
        ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
      }
      // Meta dedup id must ride in the 4th options arg (fbevents `eid`), not the
      // data object — in the data object it only lands in cd[eventID] and never
      // deduplicates against the CAPI/SGTM event_id. Omit the 4th arg entirely
      // when there is no id so we never pass a trailing undefined.
      if (eventId) {
        window.fbq('track', eventType, fbEventData, { eventID: eventId })
      } else {
        window.fbq('track', eventType, fbEventData)
      }
    }
  }, EVENT_DELAY)
}

// Fire PageView per pixel via `trackSingle` (each pixel individually, avoiding the race where
// secondary pixels miss the event) carrying the shared `eventId` in the 4th options arg so it
// deduplicates against the CAPI/SGTM PageView. Omit the options arg when there is no id so we
// never pass a trailing undefined.
const logFbPixelPageView = (eventId?: string) => {
  executeFireAndForgotTask(() => {
    const fbq = window.fbq
    if (!fbq) return
    getAllFbPixelIds().forEach(pixelId => {
      if (eventId) {
        fbq('trackSingle', pixelId, 'PageView', {}, { eventID: eventId })
      } else {
        fbq('trackSingle', pixelId, 'PageView')
      }
    })
  }, EVENT_DELAY)
}

export const formatRevenue = (price: number | null) => {
  if (price != null) {
    return price
  }
  return 0
}
